import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpEvent,
} from '@angular/common/http';
import { Observable, combineLatest, from, of } from 'rxjs';
import { switchMap, take, catchError } from 'rxjs/operators';
import { SupabaseClientService } from '../services/client/supabase-client.service';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth/auth.service';
import { UserProfile } from '@app-libs/model';
import { Session } from '@supabase/supabase-js';

@Injectable()
export class ApiAuthInterceptor implements HttpInterceptor {
  private readonly PROFILE_ENDPOINTS = ['/profile', '/profile/workspace'];

  constructor(
    private supabaseService: SupabaseClientService, 
    private authService: AuthService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for non-backend requests
    if (!request.url.startsWith(environment.BACKEND_API_URL)) {
      return next.handle(request);
    }

    // Check if this is a profile endpoint request
    const isProfileRequest = this.isProfileEndpoint(request.url);
    
    if (isProfileRequest) {
      // For profile requests, only add auth token without waiting for profile
      return this.handleProfileRequest(request, next);
    }

    // For non-profile requests, wait for both session and profile
    return this.handleStandardRequest(request, next);
  }

  private isProfileEndpoint(url: string): boolean {
    // Extract the path after the base URL
    const path = url.replace(environment.BACKEND_API_URL, '');
    return this.PROFILE_ENDPOINTS.some(endpoint => 
      path === endpoint || path.startsWith(endpoint + '?')
    );
  }

  private handleProfileRequest(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.supabaseService.getSession()).pipe(
      catchError(() => of(null)), // Continue without auth if session fails
      switchMap((session: Session | null) => {
        const headers = this.buildAuthHeaders(session);
        const modifiedRequest = request.clone({ setHeaders: headers });
        return next.handle(modifiedRequest);
      })
    );
  }

  private handleStandardRequest(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return combineLatest([
      from(this.supabaseService.getSession()).pipe(catchError(() => of(null))),
      this.authService.getUserProfile().pipe(take(1))
    ]).pipe(
      switchMap(([session, profile]: [Session | null, UserProfile]) => {
        const headers = this.buildAuthHeaders(session);
        
        // Add workspace header if available
        const workspaceId = profile?.selected_workspace || profile?.default_workspace;
        if (workspaceId) {
          headers['x-workspace-id'] = workspaceId;
        }

        const modifiedRequest = request.clone({ setHeaders: headers });
        return next.handle(modifiedRequest);
      })
    );
  }

  private buildAuthHeaders(session: Session | null): Record<string, string> {
    const headers: Record<string, string> = {};
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    return headers;
  }
}
